/* Core & libraries */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import moment from "moment";
import Skeleton from "react-loading-skeleton";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../../resources/validations/validations";
import { useCompaniesService } from "../../../../services/companiesService";
import { useGeneralService } from "../../../../services/generalService";
import { useUserContext } from "../../../../contexts/userContext";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import {
  AIRLINES,
  PAGE_TITLES,
} from '../../../../constants/appConstants';
import { HOME_ROUTE } from "../../../../constants/routes.js";
import { useLocaleContext } from "../../../../contexts/localeContext";
import eventEmitter from '../../../../services/eventEmitterService';
import { moveByPriority, isValidTaxId, getFrenchLocalizedStates } from "../../../../helpers/general";

/* Components */
import UCButton from "../../../buttons";
import DeleteCompanyMasterModal from "../../../modals/deleteCompanyMasterModal";
import UCSelect from "../../../ucselect";
import GeneralTabSkeleton from "../../skeletonGeneralTab";
import { ReactComponent as GreenCheckmarkIcon } from "../../../../resources/images/icon-notification-ok.svg";
import { ReactComponent as ErrorIcon } from "../../../../resources/images/red-x-icon.svg";
import { ReactComponent as KeyIcon } from "../../../../resources/images/green-key-icon.svg";
import { ReactComponent as EditEmailSvg } from '../../../../resources/images/edit.svg';
import { ReactComponent as DeleteCompanyIcon } from "../../../../resources/images/delete-company-icon.svg";
import { ReactComponent as WarningCompanyDeleteIcon } from "../../../../resources/images/warning-company-delete-icon.svg";
import { ReactComponent as KeyModalIcon } from "../../../../resources/images/key-icon.svg";
import ConfirmActionModal from "../../../modals/confirmActionModal/index.js";
import UpdateCitiEmailModal from "../../../admin/companyDetailsTabbedNav/updateCitiEmailModal";
import UCPhoneCountrySelector from "../../../ucPhoneCountrySelector";

/* Styles */
import styles, { countrySelect } from "./styles.js"
import { colors } from "../../../../constants/colors";

const messages = defineMessages({
  statusPlaceholderMessage: {
    id: 'settings.statusPlaceholder',
  },
  namePlaceholderMessage: {
    id: 'settings.namePlaceholder',
  },
  countryPlaceholderMessage: {
    id: 'settings.countryPlaceholder',
  },
  address1PlaceholderMessage: {
    id: 'settings.address1Placeholder',
  },
  address2PlaceholderMessage: {
    id: 'settings.address2Placeholder',
  },
  cityPlaceholderMessage: {
    id: 'settings.cityPlaceholder',
  },
  statePlaceholderMessage: {
    id: 'settings.statePlaceholder',
  },
  postalCodePlaceholderMessage: {
    id: 'settings.postalCodePlaceholder',
  },
  removePlaceholder: {
    id: 'settings.removePlaceholder',
  },
  accountIdPlaceholderMessage: {
    id: 'settings.accountIdPlaceholder',
  },
  taxIdPlaceholderMessage: {
    id: 'settings.taxId.titleAC',
  },
  businessNrPlaceholderMessage: {
    id: 'settings.businessNrPlaceholder',
  },
  industryTypePlaceholderMessage: {
    id: 'settings.industryPlaceholder',
  },
  companySizePlaceholderMessage: {
    id: 'companyAdditional.companySizePlaceholder',
  },
});


const GeneralTabAC = (props) => {

  const theme = useTheme();
  const classes = styles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();
  const globalLocale = useLocaleContext();
  const { updateCompany, deleteCompany, getCompanyAdmins, getCompanyDetails, setOverrideThreshold, getMembershipOptions } = useCompaniesService();
  const { getStates, getCountries } = useGeneralService();

  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState(null);

  const { companyInfoMockedApiValidationObjAC } = useValidationService()
  const [testSchema, setTestSchema] = useState({});

  const [showOverride, setShowOverrideModal] = useState(false);
  const [showRevert, setShowRevertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [states, setStates] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState([])
  const [showUpdateCitiEmailModal, setShowUpdateCitiEmailModal] = useState(false);
  const [fullName, setFullName] = useState("")
  const [countriesData, setCountriesData] = useState([]);
  const [optionsData, setOptionsData] = useState([]);

  const userDetails = useUserContext();
  const { initAnalytics } = useAnalytics();
  const { checkPermission } = useHasPermission();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const isPrimaryContact = userDetails?.userInfo?.companyInfo?.isPrimaryContact;

  const getOptionsSorted = (optionsData, optionName) =>
    optionsData
      ?.find((item) => item.optionName === optionName)
      ?.options?.map(({ optionValue, optionText }) => ({
        value: optionValue,
        labelId: optionText,
        label: optionValue,
      })) || [];

  const industryOptions = useMemo(() => getOptionsSorted(optionsData, "O_COMPANYSETTINGS_INDUSTRY"), [optionsData]);
  const companySizeOptions = useMemo(() => getOptionsSorted(optionsData, "O_COMPANYSETTINGS_COMPANYSIZE"), [optionsData]);


  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["settings"]
    };
    initAnalytics(initUtagData, 'init call', 'view')

    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(companyInfoMockedApiValidationObjAC);
    setTestSchema(result)
  }, [])

  useEffect(() => {
    (async () => {
      const response = await getCompanyDetails(companyEntityId);
      if (response?.address?.country) {
        handleCountryChange(response.address.country);
      } else {
        handleCountryChange("US")
      }
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
      const responseAdmins = await getCompanyAdmins(companyEntityId);
      setCompanyAdmins(responseAdmins?.usersByRoleDTOList)
      const countriesResponse = await getCountries();
      setCountriesData(moveByPriority(countriesResponse, "value") || []);
      const optionsResponse = await getMembershipOptions([
        "O_COMPANYSETTINGS_INDUSTRY",
        "O_COMPANYSETTINGS_COMPANYSIZE",
      ]);
      setOptionsData(optionsResponse || []);
    })();
  }, [])

  useEffect(() => {
    const name = companyAdmins?.find(item => item.userEntityId === companyDetails?.primaryAdminId)
    const fullName = name?.firstName + " " + name?.lastName
    setFullName(fullName)
  }, [companyAdmins]);

  useEffect(() => {
    const handleCompanyDeletionPending = async () => {
      const response = await getCompanyDetails(companyEntityId);
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
    };

    eventEmitter.on('companyDeletionPending', handleCompanyDeletionPending);

    return () => {
      eventEmitter.off('companyDeletionPending', handleCompanyDeletionPending);
    };
  }, [companyEntityId, getCompanyDetails]);

  useEffect(() => {
    if (companyDetails?.address?.country) {
      handleCountryChange(companyDetails.address.country);
    }
  }, [globalLocale]);

  const taxFieldValidation = (values) => {
    let error;
    let taxId = values.taxId;
    if (!isValidTaxId(taxId, values.countryCode)) {
      error = <FormattedMessage id="taxIdValidation.taxMatches" />;
    }
    return error;
  }

  const submitForm = async (values, setSubmitting, setFieldError) => {
    const payload = {
      companyId: companyDetails.companyId,
      primaryAdminId: values.primaryContact,
      companyName: values.companyName,
      taxId: values.taxId,
      phoneCountryCode: values.phoneCountryCode,
      phoneNumber: values.phoneNumber,
      address: {
        country: values.countryCode,
        streetAddress: values.streetAddress1,
        streetAddress2: values.streetAddress2,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
      },
      industryType: values.industryType,
      companySize: values.companySize,
    };

    if (companyDetails.canManageTaxId) {
      payload.taxId = values.taxId;
    }
    const response = await updateCompany(payload);

    setSubmitting(false);
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const goBack = () => {
    navigate(-1);
  }

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm()
    }
    navigate(HOME_ROUTE);
    window.scrollTo(0, 0);
  }

  const handleCountryChange = async (item) => {
    const response = await getStates(item);
    if (response) {
      setStates(getFrenchLocalizedStates(response.states, item, globalLocale));
    }
  }

  let countries;
  switch (providerCode) {
    case AIRLINES.AA:
      countries = [
        { value: "US", label: intl.formatMessage({ id: "countries.unitedStates" }) },
        { value: "CA", label: intl.formatMessage({ id: "countries.canada" }) },
      ];
      break;
    default:
      countries = countriesData;
      break;
  }

  const thMet = companyDetails?.thresholdMet;
  const overriden = companyDetails?.thresholdOverriden;

  const getStatusTitle = () => {
    if (thMet) {
      return <FormattedMessage id="override.threshold.met" />
    } else {
      return <FormattedMessage id="override.threshold.not.met" />
    }
  }

  const milisecondsTimestamp = companyDetails?.thresholdOverridenTimestamp * 1000;

  const getStatusDescription = () => {
    if (thMet || !overriden) {
      //to do change last check date hwne available !
      // return <FormattedMessage id="override.threshold.lastCheck" values={{date: "4/14/24, 12:15 PM"}}/>
      return null
    } else {
      if (companyDetails?.thresholdOverridenBy === "Discount tier")
        return <FormattedMessage id="override.threshold.overridden.discount" values={{ date: moment(milisecondsTimestamp).locale(globalLocale).format('M/D/YY, h:mm A') }} />
      else
        return <FormattedMessage id="override.threshold.overridden" values={{ date: moment(milisecondsTimestamp).locale(globalLocale).format('M/D/YY, h:mm A'), name: companyDetails?.thresholdOverridenBy }} />
    }
  }

  const getStatusIcon = () => {
    if (thMet) {
      return <GreenCheckmarkIcon />
    } else if (!thMet && !overriden) {
      return <ErrorIcon />
    } else if (overriden) {
      return <KeyIcon />
    }
  }

  const fetchCompanyDetails = async () => {
    const response = await getCompanyDetails(companyEntityId);
    setCompanyDetails({
      ...response,
      companyId: companyEntityId,
    });
  };

  const confirmOverride = async () => {
    await setOverrideThreshold(companyEntityId, true)
    setShowOverrideModal(false)
    fetchCompanyDetails()
  }

  const confirmRevert = async () => {
    await setOverrideThreshold(companyEntityId, false)
    setShowRevertModal(false)
    fetchCompanyDetails()
  }

  const maskedTaxId = (taxNumber) => {
    if (!taxNumber) return taxNumber;
    const str = String(taxNumber);
    return '*'.repeat(Math.max(0, str.length - 4)) + str.slice(-4);
  };

  return (
    <>
      <div className={classes.details}>
        <div>
          <div className={classes.title}>
            <FormattedMessage id="settings.titleSection" />
          </div>
          <div className={classes.description}>
            <FormattedMessage id="settings.description" />
          </div>
        </div>
        <div className={classes.btnContainer}>
          {companyDetails && companyAdmins?.length > 0 && states.length > 0 ? (
            <>
              <UCButton
                type="default"
                className={classes.buttonCancel}
                onClick={cancelUpdate}
                text={<FormattedMessage id="settings.cancel" />}
              />
              {checkPermission('F_COMPANYSETTINGS_EDIT') && (
                <UCButton
                  type="primary"
                  className={classes.button}
                  onClick={handleFormSubmit}
                  text={<FormattedMessage id="settings.save" />}
                  spinner={{
                    name: 'updateCompanySpinner',
                    size: 20,
                    color: colors.white,
                  }}
                />
              )}
            </>
          ) : (
            <div className={classes.secondRowAuto}>
              <Skeleton count={1} width={68} className={classes.longSkM} />
              <Skeleton count={1} width={68} className={classes.longSk} />
            </div>
          )}
        </div>
      </div>

      <div className={classes.dividerContainer}>
        <hr className={classes.border} />
      </div>
      {companyDetails && companyAdmins?.length > 0 && states.length > 0 ? (
        <div className={classes.editCompany}>
          {companyDetails && (
            <Formik
              innerRef={formRef}
              enableReinitialize={true}
              initialValues={{
                primaryContact: companyDetails?.primaryAdminId,
                companyName: companyDetails?.companyName,
                countryCode: companyDetails?.address?.country,
                streetAddress1: companyDetails?.address?.streetAddress,
                streetAddress2: companyDetails?.address?.streetAddress2
                  ? companyDetails?.address?.streetAddress2
                  : '',
                city: companyDetails?.address?.city,
                state: companyDetails?.address?.state,
                postalCode: companyDetails?.address?.postalCode,
                phoneCountryCode: companyDetails?.phoneCountryCode
                  ? companyDetails?.phoneCountryCode
                  : 'US',
                phoneNumber: companyDetails?.phoneNumber?.trim() || '',
                taxId: companyDetails?.taxId ? companyDetails?.taxId : '',
                industryType: companyDetails?.industryType || '',
                companySize: companyDetails?.companySize || '',
                termsAcceptedBy: companyDetails?.termsAcceptedInfo
                  ? `${companyDetails.termsAcceptedInfo.firstName} ${companyDetails.termsAcceptedInfo.lastName} · ${companyDetails.termsAcceptedInfo.emailAddress}`
                  : '',
                termsAcceptedOn: companyDetails?.termsAcceptedInfo?.termsAcceptedOn
                  ? (() => {
                      const parts = companyDetails.termsAcceptedInfo.termsAcceptedOn.split(' ');
                      return parts.length >= 2
                        ? `${parts[0]} · ${parts[1]} UTC`
                        : companyDetails.termsAcceptedInfo.termsAcceptedOn;
                    })()
                  : '',
              }}
              validationSchema={testSchema}
              onSubmit={(values, { setSubmitting, setFieldError }) => {
                submitForm(values, setSubmitting, setFieldError);
              }}
            >
              {({
                values,
                errors,
                touched,
                setFieldTouched,
                handleChange,
                handleBlur,
                handleSubmit,
                resetForm,
                isSubmitting,
                setFieldValue,
                validateField,
                enableReinitialize,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="settings.contact" />
                        </div>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.contactDescription" />
                        </div>
                      </div>
                      {companyAdmins?.length > 0 && !companyDetails?.mustChangeEmailAddress && (
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <Field
                              placeholder={'primaryContact'}
                              type="select"
                              name="primaryContact"
                              id="primaryContact"
                              render={({ form }) => (
                                <div>
                                  <label htmlFor="primaryContact" className={classes.label}>
                                    <FormattedMessage id="settings.primaryAdmin" />
                                  </label>
                                  <UCSelect
                                    className={classes.roles}
                                    displayName={<FormattedMessage id="settings.primaryAdmin" />}
                                    options={companyAdmins}
                                    defaultValue={companyAdmins?.find(
                                      (item) => item.userEntityId === values.primaryContact,
                                    )}
                                    onChange={(item) =>
                                      form.setFieldValue('primaryContact', item.userEntityId)
                                    }
                                    getOptionLabel={(option) => {
                                      return (
                                        <div className={classes.adminLabel}>
                                          <div className={classes.adminName}>
                                            {option.firstName} {option.lastName}
                                          </div>
                                        </div>
                                      );
                                    }}
                                    getOptionValue={(option) => option.userEntityId}
                                    disabled={
                                      !companyDetails?.canUpdatePrimaryAdmin ||
                                      !checkPermission('F_COMPANYSETTINGS_EDIT')
                                    }
                                  />
                                  <ErrorMessage
                                    name="primaryContact"
                                    component="span"
                                    className={classes.error}
                                  />
                                  <p className={classes.pContactSubtext}>
                                    <FormattedMessage id="settings.primaryContact.subText" />
                                  </p>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {companyDetails?.mustChangeEmailAddress && (
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputMailContainer}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="admin.editCompany.primaryAdmin" />
                              </label>
                            </div>
                            <div className={classes.editDiv}>
                              <Field
                                placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                                type="text"
                                name="travelManager"
                                id="travelManager"
                                value={fullName}
                                className={
                                  errors.travelManager && touched.travelManager
                                    ? 'input-error'
                                    : null
                                }
                                disabled={true}
                              />
                              <UCButton
                                onClick={() => setShowUpdateCitiEmailModal(true)}
                                className={classes.editButton}
                                iconEnd={<EditEmailSvg />}
                              />
                            </div>
                            <ErrorMessage
                              name="travelManeger"
                              component="span"
                              className={classes.error}
                            />
                            <span className={classes.taxIdNote}>
                              <FormattedMessage id={'admin.updateCitiEmailText'} />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="settings.location" />
                        </div>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.update" />
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="companyName" className={classes.label}>
                              <FormattedMessage id="settings.companyName" />
                              <span className={classes.required}>*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                              type="text"
                              name="companyName"
                              id="companyName"
                              className={
                                errors.companyName && touched.companyName ? 'input-error' : null
                              }
                              disabled={
                                !checkPermission('F_COMPANYSETTINGS_EDITNAME') ||
                                !checkPermission('F_COMPANYSETTINGS_EDIT')
                              }
                            />
                            <ErrorMessage
                              name="companyName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="country" className={classes.label}>
                              <FormattedMessage id="settings.country" />
                              <span className={classes.required}>*</span>
                            </label>
                            <Field
                              type="select"
                              name="countryCode"
                              id="countryCode"
                              render={({ form }) => (
                                <div>
                                  <UCSelect
                                    isSearchable={true}
                                    className={classes.roles}
                                    styles={countrySelect}
                                    displayName={
                                      <FormattedMessage id="settings.countryPlaceholder" />
                                    }
                                    options={countries}
                                    defaultValue={countries.find(
                                      (item) => item.value === values.countryCode,
                                    )}
                                    value={values.countryCode}
                                    getOptionLabel={(option) => option.label}
                                    onBlur={() => setFieldTouched('countryCode', true)}
                                    onChange={(item) => {
                                      form.setFieldValue('countryCode', item.value);
                                      handleCountryChange(item.value);
                                      {
                                        item.value !== values.countryCode
                                          ? form.setFieldValue('state', ' ')
                                          : null;
                                      }
                                    }}
                                    disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                                    error={errors.countryCode && touched.countryCode}
                                  />
                                  <ErrorMessage
                                    name="countryCode"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              )}
                              className={
                                errors.countryCode && touched.countryCode ? 'input-error' : null
                              }
                            />
                          </div>

                          {companyDetails.canManageTaxId && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="taxId" className={classes.label}>
                                <FormattedMessage id="settings.taxId.titleAC" />
                                <span className={classes.required}>*</span>
                              </label>
                              <Field
                                placeholder={
                                intl.formatMessage(messages.taxIdPlaceholderMessage)
                                }
                                type="text"
                                name="taxId"
                                id="taxId"
                                value={isPrimaryContact ? values.taxId : maskedTaxId(values.taxId)}
                                validate={() => taxFieldValidation(values)}
                                className={errors.taxId && touched.taxId ? 'input-error' : null}
                                disabled={
                                  !isPrimaryContact ||
                                  !companyDetails.canManageTaxId ||
                                  companyDetails?.companyStatus === 'REMOVED' ||
                                  !checkPermission('F_COMPANYSETTINGS_EDIT')
                                }
                              />
                              <ErrorMessage
                                name="taxId"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          )}

                          <div className={classes.phoneInputContainer}>
                            <label htmlFor="phoneNumber" className={classes.label}>
                              <FormattedMessage id="settings.phoneNumber" />
                              <span className={classes.required}>*</span>
                            </label>
                            <UCPhoneCountrySelector
                              providerCode={providerCode}
                              errors={errors}
                              touched={touched}
                              values={values}
                              disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                            />
                            <ErrorMessage
                              name="phoneNumber"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="streetAddress1" className={classes.label}>
                              <FormattedMessage id="settings.address1" />
                              <span className={classes.required}>*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.address1PlaceholderMessage)}
                              type="text"
                              name="streetAddress1"
                              id="streetAddress1"
                              className={
                                errors.streetAddress1 && touched.streetAddress1
                                  ? 'input-error'
                                  : null
                              }
                              disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                            />
                            <ErrorMessage
                              name="streetAddress1"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="streetAddress2" className={classes.label}>
                              <FormattedMessage id="settings.address2" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.address2PlaceholderMessage)}
                              type="text"
                              name="streetAddress2"
                              id="streetAddress2"
                              className={
                                errors.streetAddress2 && touched.streetAddress2
                                  ? 'input-error'
                                  : null
                              }
                              disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                            />
                            <ErrorMessage
                              name="streetAddress2"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="city" className={classes.label}>
                              <FormattedMessage id="settings.city" />
                              <span className={classes.required}>*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.cityPlaceholderMessage)}
                              type="text"
                              name="city"
                              id="city"
                              className={errors.city && touched.city ? 'input-error' : null}
                              disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                            />
                            <ErrorMessage name="city" component="span" className={classes.error} />
                          </div>
                          {states && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="state" className={classes.label}>
                                <FormattedMessage id="settings.state.AC" />
                                <span className={classes.required}>*</span>
                              </label>
                              <Field
                                type="select"
                                name="state"
                                id="state"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      className={classes.roles}
                                      displayName={
                                        <FormattedMessage id="settings.statePlaceholder.AC" />
                                      }
                                      value={values.state}
                                      options={states}
                                      onBlur={() => setFieldTouched('state', true)}
                                      defaultValue={states.find(
                                        (item) => item.value === values.state,
                                      )}
                                      onChange={(item) => form.setFieldValue('state', item.value)}
                                      isSearchable={true}
                                      error={errors.state && touched.state}
                                      disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                                    />
                                    <ErrorMessage
                                      name="state"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                                className={errors.state && touched.state ? 'input-error' : null}
                              />
                            </div>
                          )}
                          <div
                            className={`${classes.inputContainer} ${classes.lastInputContainer}`}
                          >
                            <label htmlFor="postalCode" className={classes.label}>
                              <FormattedMessage id="settings.postalCode" />
                              <span className={classes.required}>*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(
                                messages.postalCodePlaceholderMessage,
                              )}
                              type="postalCode"
                              name="postalCode"
                              id="postalCode"
                              className={
                                errors.postalCode && touched.postalCode ? 'input-error' : null
                              }
                              disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                            />
                            <ErrorMessage
                              name="postalCode"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="companyAdditional.title" />
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          {industryOptions.length > 0 && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="industryType" className={classes.label}>
                                <FormattedMessage id="companyAdditional.industry" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(
                                  messages.industryTypePlaceholderMessage,
                                )}
                                type="select"
                                name="industryType"
                                id="industryType"
                              >
                                {({ form }) => (
                                  <>
                                    <UCSelect
                                      isSearchable={false}
                                      fieldHasError={errors.industryType && touched.industryType}
                                      displayName={
                                        <FormattedMessage id="settings.industryPlaceholder" />
                                      }
                                      value={values.industryType}
                                      options={industryOptions}
                                      defaultValue={industryOptions.find(
                                        (item) => item.value === values.industryType,
                                      )}
                                      onChange={(item) =>
                                        form.setFieldValue('industryType', item.value)
                                      }
                                      disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                                    />
                                    <ErrorMessage
                                      name="industryType"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </>
                                )}
                              </Field>
                            </div>
                          )}
                          {companySizeOptions.length > 0 && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="companySize" className={classes.label}>
                                <FormattedMessage id="companyAdditional.companySize" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(
                                  messages.companySizePlaceholderMessage,
                                )}
                                type="select"
                                name="companySize"
                                id="companySize"
                              >
                                {({ form }) => (
                                  <>
                                    <UCSelect
                                      isSearchable={false}
                                      fieldHasError={errors.companySize && touched.companySize}
                                      displayName={
                                        <FormattedMessage id="companyAdditional.companySizePlaceholder" />
                                      }
                                      value={values.companySize}
                                      options={companySizeOptions}
                                      defaultValue={companySizeOptions.find(
                                        (item) => item.value === values.companySize,
                                      )}
                                      onChange={(item) =>
                                        form.setFieldValue('companySize', item.value)
                                      }
                                      disabled={!checkPermission('F_COMPANYSETTINGS_EDIT')}
                                    />
                                    <ErrorMessage
                                      name="companySize"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </>
                                )}
                              </Field>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-xl-4">
                        <h2 className={classes.location}>
                          <FormattedMessage id="settings.membership" />
                        </h2>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.membershipDescription" />
                        </div>
                      </div>
                      <div className="col-12 col-xl-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="termsAcceptedBy" className={classes.label}>
                              <FormattedMessage id="settings.termsAcceptedBy" />
                            </label>
                            <Field
                              type="text"
                              name="termsAcceptedBy"
                              id="termsAcceptedBy"
                              disabled={true}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="termsAcceptedOn" className={classes.label}>
                              <FormattedMessage id="settings.termsAcceptedOn" />
                            </label>
                            <Field
                              type="text"
                              name="termsAcceptedOn"
                              id="termsAcceptedOn"
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  {checkPermission('F_COMPANY_DELETE') && companyDetails?.companyStatus && (
                    <div>
                      <div className={classes.formCont}>
                        <div className="row">
                          <div className="col-12 col-md-4">
                            <div className={classes.location}>
                              <FormattedMessage id="settings.deleteBusiness" />
                            </div>
                            <div className={classes.description}>
                              <FormattedMessage id="settings.deleteBusinessDescription" />
                            </div>
                          </div>

                          {companyDetails?.companyStatus === 'ACTIVE' && (
                            <div className="col-12 col-md-8">
                              <UCButton
                                type="danger"
                                className={classes.deleteCompanyButton}
                                text={<FormattedMessage id="settings.deleteCompanyButton" />}
                                onClick={() => setShowDeleteModal(true)}
                              />
                            </div>
                          )}

                          {companyDetails?.companyStatus === 'PENDING_REMOVAL' && (
                            <div className="col-12 col-md-8">
                              <div className="row">
                                <div
                                  className={`col-12 col-md-auto ${classes.warningIconContainer}`}
                                >
                                  <WarningCompanyDeleteIcon />
                                </div>
                                <div className="col-12 col-md">
                                  <p className={classes.accountClosedTitle}>
                                    <FormattedMessage id="settings.accountClosedTitle" />
                                  </p>
                                  <p className={classes.accountClosedDescription}>
                                    <FormattedMessage
                                      id="settings.accountClosedPendingDescription"
                                      values={{
                                        atmName: (
                                          <span className={classes.accountClosedCustomValues}>
                                            {companyDetails?.removalRequestedByFirstName}{' '}
                                            {companyDetails?.removalRequestedByLastName}
                                          </span>
                                        ),
                                        removalDate: (
                                          <span className={classes.accountClosedCustomValues}>
                                            {companyDetails?.removalDate}
                                          </span>
                                        ),
                                      }}
                                    />
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={classes.dividerContainer}>
                        <hr className={classes.border} />
                      </div>
                    </div>
                  )}

                  <Row>
                    <Col>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.buttonCancel}
                            onClick={cancelUpdate}
                            text={<FormattedMessage id="settings.cancel" />}
                          />
                      {checkPermission('F_COMPANYSETTINGS_EDIT') && (
                          <UCButton
                            type="primary"
                            className={classes.button}
                            onClick={handleFormSubmit}
                            text={<FormattedMessage id="settings.save" />}
                            spinner={{
                              name: 'updateCompanySpinner',
                              size: 20,
                              color: colors.white,
                            }}
                          />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              )}
            </Formik>
          )}

          {showDeleteModal && (
            <DeleteCompanyMasterModal
              showModal={showDeleteModal}
              setShowModal={setShowDeleteModal}
              modalIcon={<DeleteCompanyIcon />}
              setSelectedRow={(value) => console.log(value)}
            />
          )}

          <ConfirmActionModal
            showModal={showOverride}
            setShowModal={setShowOverrideModal}
            modalIcon={<KeyModalIcon />}
            modalTitle={<FormattedMessage id="override.threshold.modal.title" />}
            modalDescription={<FormattedMessage id="override.threshold.modal.description" />}
            submitCallback={confirmOverride}
            btnSpinner="overrideThresholdSpinner"
          />

          <ConfirmActionModal
            showModal={showRevert}
            setShowModal={setShowRevertModal}
            modalIcon={<KeyModalIcon />}
            modalTitle={<FormattedMessage id="override.threshold.revert.modal.title" />}
            modalDescription={<FormattedMessage id="override.threshold.revert.modal.description" />}
            submitCallback={confirmRevert}
            btnSpinner="revertThresholdSpinner"
          />
          {companyDetails?.mustChangeEmailAddress && (
            <UpdateCitiEmailModal
              showUpdateCitiEmailModal={showUpdateCitiEmailModal}
              setShowUpdateCitiEmailModal={setShowUpdateCitiEmailModal}
              companyDetails={companyDetails}
            />
          )}
        </div>
      ) : (
        <GeneralTabSkeleton />
      )}
    </>
  );
}

export default GeneralTabAC;
